import {t} from '@lingui/macro';
import {useModal} from 'extensions/viewport/useModal';
import {truncateFileName} from 'fast-sdk/src/transfers/utils';
import {Prompt} from 'interface/common/Prompt';
import {useCallback} from 'react';
import {useToast} from 'react-native-toast-notifications';
import {rename, renameShare} from 'utils/fast/storage';

import type {StorageNamespace} from 'fast-sdk/src/api/storage/consts';
import {StorageItemType} from 'fast-sdk/src/api/storage/consts';

export function useModalRename(
  id: string,
  name: string,
  type: string,
  workspaceId: string,
  namespace: StorageNamespace,
  itemType?: string,
  targetId?: string,
) {
  const modal = useModal();
  const toast = useToast();

  const submit = useCallback(
    async (value: string) => {
      const newName = truncateFileName(value);
      if (newName) {
        let result = false;

        // Debug logging to understand what's happening
        console.log('🔍 Rename Debug Info:', {
          itemType,
          targetId,
          id,
          name,
          newName,
          isLink: itemType === StorageItemType.Link,
          hasTargetId: !!targetId,
          condition: itemType === StorageItemType.Link && targetId,
        });

        // Check if this is a share/link that needs to be renamed using the share API
        if (itemType === StorageItemType.Link && targetId) {
          console.log('🔗 Using share rename API with targetId:', targetId);
          // For shares, use the share API with 'title' parameter
          const response = await renameShare(targetId, newName);
          console.log('🔗 Share rename response:', response);
          result = response.result;
        } else {
          console.log('📁 Using regular file rename API with id:', id);
          // For regular files/folders, use the storage API with 'name' parameter
          const response = await rename(id, newName, workspaceId, namespace);
          console.log('📁 File rename response:', response);
          result = response.result;
        }

        if (result) {
          toast.show(t`Renamed "${name}" to "${newName}"`);
        } else {
          toast.show(t`Failed to rename ${name}`, {type: 'danger'});
        }
      }
      modal.close();
    },
    [id, name, workspaceId, namespace, itemType, targetId],
  );

  const open = useCallback(() => {
    const isFolder = type === 'folder';
    const isShare = itemType === StorageItemType.Link;

    modal.open(
      <Prompt
        onClose={modal.close}
        title={t`Rename ${isShare ? 'Share' : isFolder ? 'Folder' : 'File'}`}
        text={t`Give "${name}" a new name`}
        options={{
          type: 'plain-text',
          placeholder: name,
          defaultValue: name,
          maxLength: 100,
          selectInput: isFolder ? 'all' : 'filename',
        }}
        buttons={[
          {
            variant: 'secondary',
            text: t`Cancel`,
            onPress: modal.close,
          },
          {
            variant: 'primary',
            text: t`Done`,
            onPress: submit,
            isSubmit: true,
          },
        ]}
      />,
    );
  }, [id, name, itemType]);

  return {open, close: modal.close};
}
